import { Edit, ImageSearchOutlined } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { ImageLoader, OneMedia } from '@prismamedia/one-components';
import { MediaInput } from 'components/MediaInput';
import { ResourceType } from 'pages/CampaignPage/CampaignEditPage/ContentPage/utils/useGetBlockItems';
import { FC, useMemo, useState } from 'react';
import { BodyEdge } from 'types';
import { resizeURL } from 'utils/resizer';
import { useQueryParams } from 'utils/useUriHelpers';
import { useStyles } from './styles';

interface EditableMediaProps {
  blockType: string | null | undefined;
  fallbackClassName: string;
  media: OneMedia | null | undefined;
  mediaClassName: string;
  onMediaChange: (field: string, media: OneMedia[]) => void;
  slideShowBody?: string | null;
}

export const EditableMedia: FC<EditableMediaProps> = ({
  fallbackClassName,
  media,
  mediaClassName,
  onMediaChange,
  blockType,
  slideShowBody,
}) => {
  const [isInEditMode, setEditMode] = useState<boolean>(false);
  const { queryParams } = useQueryParams<{ brand: string | null }>({
    brand: null,
  });

  const classes = useStyles({ isInEditMode });
  const imageURL = useMemo(() => {
    let canonical, extension;

    if (media) {
      canonical = media?.iframely?.meta.canonical || undefined;
      extension = media?.iframely?.meta.extension || undefined;
    }

    if (slideShowBody) {
      // eslint-disable-next-line no-case-declarations
      let parsedBody = null;
      try {
        parsedBody = JSON.parse(slideShowBody) as BodyEdge;
      } catch (error) {
        throw new Error(`Editable media body parser error : ${error}`);
      }

      canonical =
        parsedBody.blocks.length > 0 && parsedBody?.blocks[0].data.iframely
          ? parsedBody.blocks[0].data.iframely.meta.canonical
          : undefined;
      extension =
        parsedBody.blocks.length > 0 && parsedBody?.blocks[0].data.iframely
          ? parsedBody?.blocks[0].data.iframely.meta.extension
          : undefined;
    }

    return canonical ? resizeURL(canonical, extension?.toLowerCase()) : '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media]);

  const bodyMedia = useMemo(() => {
    try {
      return slideShowBody
        ? (JSON.parse(slideShowBody).blocks[0].data as OneMedia[])
        : [];
    } catch (e) {
      return [] as OneMedia[];
    }
  }, [slideShowBody]);

  return (
    <div className={classes.root}>
      {!isInEditMode && (
        <ImageLoader
          className={mediaClassName}
          image={<ImageSearchOutlined className={fallbackClassName} />}
          src={imageURL}
        />
      )}

      {isInEditMode && (
        <MediaInput
          brandKey={queryParams.brand || 'NL'}
          medias={media ? [media] : bodyMedia}
          setMedias={(updater) => {
            const medias =
              typeof updater === 'function'
                ? updater(media ? [media] : bodyMedia)
                : updater;
            onMediaChange('media', medias);
          }}
          disableFields
        />
      )}

      {blockType !== ResourceType.RECIPE && (
        <div className={classes.iconContainer}>
          <IconButton
            size="small"
            className={classes.editIcon}
            onClick={() => setEditMode((prevMode) => !prevMode)}
          >
            <Edit
              classes={{ fontSizeSmall: classes.fontSizeSmall }}
              fontSize="small"
            />
          </IconButton>
        </div>
      )}
    </div>
  );
};
